import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, Clock, User, Search, ArrowRight, BookOpen, Heart, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'nutrition', name: 'Nutrition' },
    { id: 'lifestyle', name: 'Lifestyle' },
    { id: 'technology', name: 'Technology' },
    { id: 'research', name: 'Research' },
    { id: 'tips', name: 'Tips & Tricks' },
  ];

  const blogPosts = [
    {
      id: 1,
      title: '10 Best Foods to Lower Blood Sugar Naturally',
      excerpt: 'Discover the top foods that can help manage your blood sugar levels without medication. From leafy greens to nuts, these foods are backed by science.',
      category: 'nutrition',
      author: 'Dr. Sarah Johnson',
      date: '2024-12-15',
      readTime: '5 min read',
      image: '🥗',
      featured: true,
    },
    {
      id: 2,
      title: 'How Exercise Affects Blood Sugar: A Complete Guide',
      excerpt: 'Learn how different types of exercise impact your glucose levels and how to optimize your workout routine for better diabetes management.',
      category: 'lifestyle',
      author: 'Mike Thompson',
      date: '2024-12-12',
      readTime: '7 min read',
      image: '🏃',
      featured: true,
    },
    {
      id: 3,
      title: 'The Future of Continuous Glucose Monitoring',
      excerpt: 'Explore the latest advancements in CGM technology and how AI is revolutionizing diabetes care with predictive analytics.',
      category: 'technology',
      author: 'Tech Team',
      date: '2024-12-10',
      readTime: '6 min read',
      image: '📱',
      featured: false,
    },
    {
      id: 4,
      title: 'New Study: Mediterranean Diet Reduces Diabetes Risk by 30%',
      excerpt: 'A comprehensive study involving 25,000 participants reveals the powerful effects of the Mediterranean diet on preventing Type 2 diabetes.',
      category: 'research',
      author: 'Research Desk',
      date: '2024-12-08',
      readTime: '4 min read',
      image: '📊',
      featured: false,
    },
    {
      id: 5,
      title: '5 Quick Tips for Managing Blood Sugar During Holidays',
      excerpt: 'The holiday season can be challenging for diabetics. Here are practical tips to enjoy festivities while keeping your glucose in check.',
      category: 'tips',
      author: 'DiaWise Team',
      date: '2024-12-05',
      readTime: '3 min read',
      image: '🎄',
      featured: false,
    },
    {
      id: 6,
      title: 'Understanding Carb Counting: A Beginner\'s Guide',
      excerpt: 'Master the art of carb counting with this comprehensive guide. Learn to estimate carbs accurately for better insulin dosing.',
      category: 'nutrition',
      author: 'Lisa Chen, RD',
      date: '2024-12-01',
      readTime: '8 min read',
      image: '🍞',
      featured: false,
    },
    {
      id: 7,
      title: 'Sleep and Blood Sugar: The Hidden Connection',
      excerpt: 'Poor sleep can wreak havoc on your blood sugar levels. Discover the science behind sleep and glucose regulation.',
      category: 'lifestyle',
      author: 'Dr. James Wilson',
      date: '2024-11-28',
      readTime: '5 min read',
      image: '😴',
      featured: false,
    },
    {
      id: 8,
      title: 'How to Read Food Labels Like a Pro',
      excerpt: 'Decode nutrition labels to make smarter food choices. Learn what to look for and what to avoid when shopping for groceries.',
      category: 'tips',
      author: 'Nutrition Team',
      date: '2024-11-25',
      readTime: '4 min read',
      image: '🏷️',
      featured: false,
    },
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-blue-600 py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <BookOpen className="w-10 h-10 text-white mr-3" />
            <h1 className="text-4xl font-bold text-white">DiaWise Blog</h1>
          </div>
          <p className="text-emerald-100 text-lg max-w-2xl mx-auto">
            Expert insights, tips, and the latest research on diabetes management and healthy living.
          </p>
          
          {/* Search Bar */}
          <div className="mt-8 max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/90 border-0 h-12 text-gray-900"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className={selectedCategory === category.id ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Featured Posts */}
        {selectedCategory === 'all' && searchQuery === '' && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
              <TrendingUp className="w-6 h-6 mr-2 text-emerald-600" />
              Featured Articles
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow border-0 dark:bg-gray-800">
                  <div className="bg-gradient-to-r from-emerald-500 to-blue-500 h-32 flex items-center justify-center">
                    <span className="text-6xl">{post.image}</span>
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
                        {categories.find(c => c.id === post.category)?.name}
                      </Badge>
                      <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                        Featured
                      </Badge>
                    </div>
                    <CardTitle className="text-xl hover:text-emerald-600 cursor-pointer transition-colors">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">{post.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {post.author}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {post.readTime}
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700">
                        Read More <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* All Posts */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
            <span className="text-gray-500 text-lg font-normal ml-2">({filteredPosts.length})</span>
          </h2>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No articles found. Try a different search or category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="hover:shadow-lg transition-shadow border-0 dark:bg-gray-800 flex flex-col">
                  <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 h-24 flex items-center justify-center">
                    <span className="text-4xl">{post.image}</span>
                  </div>
                  <CardHeader className="flex-1">
                    <Badge variant="outline" className="w-fit mb-2 text-xs">
                      {categories.find(c => c.id === post.category)?.name}
                    </Badge>
                    <CardTitle className="text-lg line-clamp-2 hover:text-emerald-600 cursor-pointer transition-colors">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 text-sm">{post.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <span>{post.readTime}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <Card className="mt-12 bg-gradient-to-r from-emerald-500 to-blue-500 border-0 text-white">
          <CardContent className="py-8">
            <div className="text-center max-w-xl mx-auto">
              <Heart className="w-12 h-12 mx-auto mb-4 text-white/80" />
              <h3 className="text-2xl font-bold mb-2">Stay Updated</h3>
              <p className="text-emerald-100 mb-6">
                Subscribe to our newsletter for the latest diabetes tips, research, and DiaWise updates.
              </p>
              <div className="flex gap-2 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-white/90 border-0 text-gray-900"
                />
                <Button className="bg-white text-emerald-600 hover:bg-emerald-50">
                  Subscribe
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/">
            <Button variant="outline">← Back to Home</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Blog;
